import { useMemo, useRef, useState } from 'react';

import { dates } from '@innovamat/radiance-utils';

import { useRegionParameters } from '../../contents/hooks/use-region-parameters';
import {
  is12hClock,
  type ClockFormat,
} from '../../shared/modules/region/region-parameters';
import { SCHEDULE_DEFAULT_VALUES } from '../constants';

type ScheduleValues = {
  scheduleStart: string;
  scheduleEnd: string;
  timeZone: string | null;
};

type UseScheduleValues = {
  values: ScheduleValues;
  hoursOptions: { label: string; value: string }[];
  timeZonesOptions: { label: string; value: string }[];
  areAllFieldsFilled: boolean;
  handleUpdateSchedule: (schedule: keyof ScheduleValues, value: string) => void;
  handleResetForm: () => void;
  handleInitValues: (init: ScheduleValues) => void;
  handleTimeZoneConfirmation: () => void;
  showTimeZoneConfirmation: boolean;
  startAndEndString: string;
  handleTimeZoneCancel: () => void;
};

const timeZonesOptions = dates.getAllTimeZonesWithGMT().map((timeZone) => ({
  label: `${timeZone.timeZone.replace('/', ', ')} (${timeZone.gmtOffset})`,
  value: timeZone.timeZone ?? '',
}));

export function formatHourAndMinutes(
  date: string,
  clockFormat: ClockFormat
): string {
  const [hour, minutes] = date.split(':');

  return dates.formatTime({
    dateTime: `${hour}:${minutes}`,
    hour12: is12hClock(clockFormat),
    suffix24Hour: '',
  });
}

function useClassroomScheduleValues(): UseScheduleValues {
  const { regionParameters } = useRegionParameters({});

  const [scheduleValues, setScheduleValues] = useState<ScheduleValues>(
    SCHEDULE_DEFAULT_VALUES
  );

  const [showTimeZoneConfirmation, setShowTimeZoneConfirmation] =
    useState(false);

  const temporalTimezoneValue = useRef<string>('');

  const hoursOptions = useMemo(() => {
    return dates.getDayHours().map((hour) => ({
      label: formatHourAndMinutes(hour, regionParameters?.clock),
      value: hour,
    }));
  }, [regionParameters?.clock]);

  const handleResetForm = (): void => {
    setScheduleValues(SCHEDULE_DEFAULT_VALUES);
  };

  const handleInitValues = (init: ScheduleValues): void => {
    setScheduleValues(init);
  };

  const handleUpdateSchedule = (
    schedule: keyof ScheduleValues,
    value: string
  ): void => {
    if (schedule === 'timeZone') {
      setShowTimeZoneConfirmation(true);
      temporalTimezoneValue.current = value;
    } else {
      setScheduleValues((prev) => ({ ...prev, [schedule]: value }));
    }
  };

  const handleTimeZoneConfirmation = (): void => {
    setScheduleValues((prev) => ({
      ...prev,
      timeZone: temporalTimezoneValue.current,
    }));
    setShowTimeZoneConfirmation(false);
  };

  const handleTimeZoneCancel = (): void => {
    setShowTimeZoneConfirmation(false);
    temporalTimezoneValue.current = '';
  };

  const areAllFieldsFilled = useMemo(() => {
    return Boolean(
      scheduleValues.scheduleStart &&
        scheduleValues.scheduleEnd &&
        scheduleValues.timeZone
    );
  }, [scheduleValues]);

  const startAndEndString = useMemo(() => {
    const startFormatted = formatHourAndMinutes(
      scheduleValues.scheduleStart,
      regionParameters?.clock
    );
    const endFormatted = formatHourAndMinutes(
      scheduleValues.scheduleEnd,
      regionParameters?.clock
    );

    return `${startFormatted} - ${endFormatted}`;
  }, [scheduleValues, regionParameters?.clock]);

  return {
    areAllFieldsFilled,
    handleInitValues,
    handleResetForm,
    handleUpdateSchedule,
    handleTimeZoneConfirmation,
    showTimeZoneConfirmation,
    handleTimeZoneCancel,
    hoursOptions,
    timeZonesOptions,
    startAndEndString,
    values: scheduleValues,
  };
}

export { SCHEDULE_DEFAULT_VALUES, useClassroomScheduleValues };
export type { ScheduleValues, UseScheduleValues };
